import { useLocalSearchParams } from 'expo-router';
import { type ComponentProps, useMemo } from 'react';
import { PixelRatio, Platform, StyleSheet, View } from 'react-native';

import { AnchorSectionList, MqSwitch, XStack, createMqStyles } from '@fhs/ui';
import { ScrollRestorationKey } from '@fhs/utils';

import { MenuErrorFallback } from '../components/menu-error-fallback';
import { MenuListLegalText } from '../components/menu-list-legal-text';
import { MenuListSkeletonLoading } from '../components/menu-list-skeleton';
import { MenuOffersLink } from '../components/menu-offers-link';
import { useMenuListQuery } from '../queries/use-menu-list-query';
import type { ScreenMenuListProps } from '../types';

const ORDER_TYPE_FOCUS_TARGET_ATTR = 'data-fhs-order-type-focus-target';

export function ScreenMenuList(props: ScreenMenuListProps) {
  const mqStyles = useMqStyles();

  const sections: ComponentProps<typeof AnchorSectionList>['sections'] = useMemo(
    () => [
      {
        id: 'offers-and-rewards',
        title: '',
        anchorTitle: 'Offers & Rewards',
        image: {
          asset: require('../assets/img/offers-and-rewards-icon.webp'),
          altText: 'Offers and rewards',
        },
        items: <MenuOffersLink style={mqStyles.offersLink} />,
      },
      ...props.sections.map(rawSection => {
        return {
          id: rawSection.slug,
          title: rawSection.displayName,
          anchorTitle: rawSection.displayName,
          image: {
            asset: {
              uri: rawSection.image?.asset?.uri,
              blurHash: rawSection.image?.asset?.blurHash ?? undefined,
            },
            altText: rawSection.image?.altText || rawSection.displayName || '',
          },
          items: rawSection.items.map(rawItem => {
            const baseUri = rawItem.image?.asset?.uri;
            let sizedUri: string | undefined;
            if (baseUri) {
              try {
                const imageUrl = new URL(baseUri);
                imageUrl.searchParams.set('h', '250');
                imageUrl.searchParams.set(
                  'dpr',
                  Math.min(Math.max(PixelRatio.get(), 1.75), 2.5).toString()
                );
                imageUrl.searchParams.set('fm', 'webp');
                sizedUri = imageUrl.toString();
              } catch {
                sizedUri = baseUri;
              }
            }

            return {
              id: rawItem.slug,
              href: rawItem.url,
              title: rawItem.displayName,
              description: rawItem.descriptionShort,
              badge: rawItem.badge ?? undefined,
              image: {
                asset: {
                  uri: sizedUri,
                  blurHash: rawItem.image?.asset?.blurHash ?? undefined,
                },
                altText: rawItem.image?.altText || rawItem.displayName,
              },
              isAvailable: rawItem.isAvailable,
            };
          }),
        };
      }),
    ],
    [mqStyles, props.sections]
  );

  const { section: defaultSectionId } = useLocalSearchParams<{ section: string }>();

  return (
    <AnchorSectionList
      sections={sections}
      defaultSectionId={defaultSectionId}
      scrollRestorationKey={ScrollRestorationKey.MenuList}
    >
      <MqSwitch
        $base={() => (
          <View style={styles.container}>
            <AnchorSectionList.AnchorListHorizontal />
            <AnchorSectionList.SectionList footer={<MenuListLegalText />} />
          </View>
        )}
        $gteDesktop={() => (
          <View style={styles.container}>
            <XStack style={styles.xStack}>
              <AnchorSectionList.AnchorListVertical />
              <AnchorSectionList.SectionList footer={<MenuListLegalText />} />
            </XStack>
          </View>
        )}
      />
    </AnchorSectionList>
  );
}
type ScreenMenuListWithQueryProps = {
  selectedRestaurantId: string;
  region: 'us' | 'ca';
  serviceMode: 'delivery' | 'pickup';
};

export function ScreenMenuListWithQuery({
  selectedRestaurantId: restaurantId,
  serviceMode,
  region,
}: ScreenMenuListWithQueryProps) {
  const { data, isError, isPending, isFetching } = useMenuListQuery({
    restaurantId,
    serviceMode,
    region,
  });
  const isMenuLoading = isPending || (!data && isFetching);
  const menuAccessibilityName = serviceMode === 'delivery' ? 'Delivery menu' : 'Pick up menu';
  const menuFocusTarget = (
    <MenuContentFocusTarget
      label={
        isMenuLoading ? `Loading ${menuAccessibilityName}. Please wait.` : menuAccessibilityName
      }
      busy={isMenuLoading}
    />
  );

  // Show skeleton during initial load or when refetching after a null response
  // This also prevents the error fallback from flashing when data is temporarily null
  if (isMenuLoading) {
    return (
      <>
        {menuFocusTarget}
        <MenuListSkeletonLoading />
      </>
    );
  }

  // TODO: add refresh logic
  if (isError) {
    return (
      <View style={{ flex: 1 }}>
        {menuFocusTarget}
        <MenuErrorFallback />
      </View>
    );
  }

  if (!data) {
    // XXX: we should log an error, this should never happen in production
    // @ts-ignore
    // console.error(
    //   'No data found for menu list. If you are seeing this, you many need to sync store data'
    // );
    return (
      <View style={{ flex: 1 }}>
        {menuFocusTarget}
        <MenuErrorFallback />
      </View>
    );
  }

  return (
    <>
      {menuFocusTarget}
      <ScreenMenuList sections={data.sections} />
    </>
  );
}

function MenuContentFocusTarget({ label, busy }: { label: string; busy: boolean }) {
  if (Platform.OS !== 'web') {
    return null;
  }

  return (
    <View
      accessible
      accessibilityLabel={label}
      style={styles.menuFocusTarget}
      {...({
        [ORDER_TYPE_FOCUS_TARGET_ATTR]: 'true',
        'aria-busy': busy,
        'aria-label': label,
        'aria-level': 1,
        'aria-live': 'polite',
        role: 'heading',
        tabIndex: -1,
      } as Record<string, unknown>)}
    />
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
  },
  xStack: {
    height: '100%',
  },
  offersLink: {
    marginBottom: 20,
  },
  menuFocusTarget: {
    borderWidth: 0,
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1,
  },
});

const useMqStyles = createMqStyles({
  offersLink: {
    $base: {
      marginBottom: 0,
      overflow: 'hidden',
    },
    $gteDesktop: {
      marginBottom: 0,
      marginTop: 4,
    },
  },
});
